import React, { FC, useMemo } from 'react';
import { Offer, PaymentPlace, useAnalytics } from '@funtech-int/yango-shared';
import cn from 'classnames/bind';
import { useInView } from 'react-intersection-observer';

import { PurchaseButton } from '@/shared/types/notifications';
import { Button, ButtonSize } from '@/shared/components/Button/Button';
import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { InfoBlockContentType } from '@/shared/types/info-block';
import { getOfferPositionsData } from '@/shared/lib/analytics';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { createSubscriptionOfferAnalytics } from '@/scenarios/home/lib/subscriptionOfferAnalytics';

import { getSubscriptionOfferButtonText } from '../../../lib/getSubscriptionOfferButtonText';
import { useMultiOfferSuccessPurchase } from '../../../hooks/useMultiOfferSuccessPurchase';
import { useI18n } from '../../../hooks/useOnboardingI18n';

import styles from './InfoBlockPrimaryPurchaseButton.module.css';

const cx = cn.bind(styles);

type InfoBlockButtonAlignment = 'default' | 'centered';

type InfoBlockPrimaryPurchaseButton = Partial<PurchaseButton>;

type BaseProps = InfoBlockPrimaryPurchaseButton & {
    size: ButtonSize;
    contentType: InfoBlockContentType;
    alignment: InfoBlockButtonAlignment;
};

type ContainerProps = BaseProps & {
    offer: Offer | null | undefined;
    paymentPlace?: PaymentPlace;
};

type ComponentProps = BaseProps & {
    offer: Offer;
    paymentPlace?: PaymentPlace;
};

const InfoBlockPrimaryPurchaseButtonComponent: FC<ComponentProps> = ({
    size,
    contentType,
    alignment,
    offer,
    paymentPlace,
}) => {
    const i18n = useI18n();
    const { batchId, positionId } = offer.analyticData;
    const { offerTexts } = offer;
    const billingFeatureNames = offer.requestedFeatures;
    const offersPositionsData = getOfferPositionsData({
        offersBatchId: batchId,
        offersPositionId: positionId,
    });
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const onSuccessPurchase = useMultiOfferSuccessPurchase({
        billingFeatureNames,
    });

    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));

    const buttonText = getSubscriptionOfferButtonText(i18n, offerTexts?.buttonText);

    const buttonTextWithDetails = offerTexts?.buttonTextWithDetails;

    const analytics = useAnalytics((eventSender) =>
        createSubscriptionOfferAnalytics(eventSender, {
            buttonText,
            entityType: contentType,
            offer,
        })
    );
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const controller = useMemo(() => {
        function onPurchaseSuccess() {
            subscriptionRefetch();
            analytics.succeeded();
            offerWebAnalytics.purchaseSucceeded();
            onSuccessPurchase();
        }
        return {
            onOfferShow: () => {
                if (!offer) return;
                analytics.showed();
                offerWebAnalytics.offerShowed();
                paymentPlace?.analytics.offerShowed(offer);
            },
            onOfferClick: async () => {
                if (!offer) return;
                analytics.navigated();
                offerWebAnalytics.offerClicked();
                paymentPlace?.analytics.offerClicked(offer);
                await paymentPlace?.checkout(offer, {
                    onPurchaseSuccess,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
        };
    }, [
        subscriptionRefetch,
        analytics,
        offerWebAnalytics,
        onSuccessPurchase,
        offer,
        offerLoginUrl,
        paymentPlace,
    ]);

    const { ref: setElementRef } = useInView({
        triggerOnce: true,
        threshold: 0.8,
        onChange(inView: boolean) {
            if (inView) {
                controller.onOfferShow();
            }
        },
    });

    return (
        <div className={cx('root', `root_${alignment}`)} ref={setElementRef}>
            <Button
                size={size}
                variant="plus"
                onClick={controller.onOfferClick}
                {...{ contentType, offersPositionsData }}
            >
                {buttonText}
            </Button>
            {buttonTextWithDetails && (
                <div className={cx('description')}>{buttonTextWithDetails}</div>
            )}
        </div>
    );
};

export const InfoBlockPrimaryPurchaseButton: FC<ContainerProps> = ({
    offer,
    paymentPlace,
    ...props
}) => {
    if (!offer?.target) {
        return null;
    }

    return (
        <InfoBlockPrimaryPurchaseButtonComponent
            {...props}
            offer={offer}
            paymentPlace={paymentPlace}
        />
    );
};
